import { useCallback, useRef, useEffect } from 'react';
import { useConnectionState, useToastState } from '../store/useAppStore';
import { debugLogger } from '../utils/debugLogger';

interface UseStatusPollingOptions {
  interval?: number; // Polling interval in milliseconds
  maxAttempts?: number; // Maximum polling attempts before giving up
  onConnectionDetected?: () => void; // Callback when connection is detected
  onError?: (error: Error) => void; // Callback when error occurs
  enabled?: boolean; // Whether polling is enabled
}

export const useStatusPolling = ({
  interval = 10000, // 10 seconds default
  maxAttempts = 30, // 5 minutes of polling
  onConnectionDetected,
  onError,
  enabled = true
}: UseStatusPollingOptions = {}) => {
  const { isConnected, authId, setConnectionStatus } = useConnectionState();
  const { showToast } = useToastState();

  // Polling state
  const pollingInterval = useRef<number | null>(null);
  const pollingAttempts = useRef(0);
  const isPolling = useRef(false);
  const lastEnabledState = useRef(enabled);
  const lastConnectedState = useRef(isConnected);
  const debounceTimer = useRef<number | null>(null);

  const performStatusCheck = useCallback(async (): Promise<boolean> => {
    if (!authId || isConnected) {
      return isConnected;
    }

    try {
      // Create AbortController for timeout compatibility
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
      
      const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      // Clear timeout if request completes successfully
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data?.status === 'connected') {
          debugLogger.pollingEvent('connection detected', authId, { 
            attempt: pollingAttempts.current,
            verified: false,
            responseData: data 
          });
          
          console.log('📊 Status polling detected connection - verifying...');
          
          // More forgiving verification approach - trust the first check more
          setConnectionStatus(true);
          
          // Still do verification but don't fail if it doesn't work
          try {
            const verifyController = new AbortController();
            const verifyTimeoutId = setTimeout(() => verifyController.abort(), 3000); // Shorter timeout
            
            const verifyResponse = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/status`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              signal: verifyController.signal
            });
            
            clearTimeout(verifyTimeoutId);
            
            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              if (verifyData.success && verifyData.data?.status === 'connected') {
                debugLogger.pollingEvent('connection verified', authId, { 
                  attempt: pollingAttempts.current,
                  verified: true 
                });
                console.log('✅ Status polling verification successful');
              } else {
                console.log('⚠️ Verification returned different status, but keeping original detection');
              }
            }
          } catch (verifyError) {
            console.log('📊 Connection verification failed, but trusting initial detection');
          }
          
          // Call success callback
          if (onConnectionDetected) {
            onConnectionDetected();
          }
          
          // Stop polling
          stopPolling();
          return true;
        }
      } else if (response.status === 429) {
        // Rate limited - slow down polling
        console.warn('📊 Status polling rate limited, slowing down');
        return false;
      } else if (response.status === 404) {
        // Not found - connection might not be established yet
        return false;
      }
    } catch (error) {
      const errorMessage = (error as Error).message;
      
      // Only call onError for unexpected errors, not timeouts or network issues
      if (!errorMessage.includes('aborted') && 
          !errorMessage.includes('Failed to fetch') && 
          !errorMessage.includes('NetworkError')) {
        console.log('📊 Status check failed:', error);
        if (onError) {
          onError(error as Error);
        }
      }
    }

    return false;
  }, [authId, isConnected, setConnectionStatus, onConnectionDetected, onError]);

  const startPolling = useCallback(() => {
    // Prevent multiple calls and validate conditions
    if (isPolling.current || !enabled || !authId || isConnected) {
      debugLogger.pollingEvent('startPolling skipped', authId, {
        isPolling: isPolling.current,
        enabled,
        hasAuthId: !!authId,
        isConnected
      });
      return;
    }

    debugLogger.pollingEvent('startPolling initiated', authId, {
      interval,
      maxAttempts,
      enabled
    });
    
    isPolling.current = true;
    pollingAttempts.current = 0;

    // Initial check with delay to avoid immediate conflicts
    setTimeout(() => {
      if (isPolling.current && !isConnected) {
        performStatusCheck();
      }
    }, 2000);

    // Set up polling interval
    pollingInterval.current = window.setInterval(async () => {
      // Double-check conditions before each attempt
      if (!isPolling.current || isConnected) {
        stopPolling();
        return;
      }

      if (pollingAttempts.current >= maxAttempts) {
        debugLogger.pollingEvent('max attempts reached', authId, {
          attempts: pollingAttempts.current,
          maxAttempts
        });
        
        stopPolling();
        
        showToast({
          type: 'info',
          title: 'Verificação Automática',
          message: 'Parou de verificar automaticamente. Use o botão "Validar Conexão" se necessário.',
          duration: 5000
        });
        return;
      }

      pollingAttempts.current++;
      const connected = await performStatusCheck();
      
      if (connected) {
        stopPolling();
      }
    }, interval);

  }, [enabled, authId, isConnected, interval, maxAttempts, performStatusCheck, showToast]);

  const stopPolling = useCallback(() => {
    if (pollingInterval.current) {
      window.clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    
    // Clear debounce timer if exists
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    
    if (isPolling.current) {
      isPolling.current = false;
      debugLogger.pollingEvent('polling stopped', authId, {
        attempts: pollingAttempts.current
      });
    }
  }, [authId]);

  const resetPolling = useCallback(() => {
    stopPolling();
    pollingAttempts.current = 0;
    
    if (enabled && authId && !isConnected) {
      startPolling();
    }
  }, [enabled, authId, isConnected, startPolling, stopPolling]);

  // Debounced effect to prevent rapid start/stop cycles
  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Check if state actually changed to avoid unnecessary operations
    const stateChanged = 
      lastEnabledState.current !== enabled || 
      lastConnectedState.current !== isConnected;

    if (!stateChanged) {
      return;
    }

    // Update refs
    lastEnabledState.current = enabled;
    lastConnectedState.current = isConnected;

    // Debounce the actual start/stop logic
    debounceTimer.current = window.setTimeout(() => {
      const shouldPoll = enabled && authId && !isConnected;
      
      debugLogger.pollingEvent('effect triggered', authId, {
        enabled,
        hasAuthId: !!authId,
        isConnected,
        shouldPoll,
        isCurrentlyPolling: isPolling.current
      });

      if (shouldPoll && !isPolling.current) {
        startPolling();
      } else if (!shouldPoll && isPolling.current) {
        stopPolling();
      }
    }, 1000); // 1 second debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [enabled, authId, isConnected, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling: isPolling.current,
    pollingAttempts: pollingAttempts.current,
    maxAttempts,
    startPolling,
    stopPolling,
    resetPolling,
    performStatusCheck
  };
};

export default useStatusPolling;